import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatPrice, formatDate, formatDateTime, localized } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Truck, PackageCheck, Clock } from "lucide-react";

const STATUS_ICON = {
  pending: <Clock className="w-3 h-3" />,
  shipped: <Truck className="w-3 h-3" />,
  partial: <Truck className="w-3 h-3" />,
  delivered: <PackageCheck className="w-3 h-3" />,
};

export default function Account() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [orders, setOrders] = useState([]);
  const [subs, setSubs] = useState([]);

  useEffect(() => {
    api.get("/orders/me").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/subscriptions/me").then((r) => setSubs(r.data)).catch(() => {});
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="account-page">
      <p className="overline">{t("account.overline")}</p>
      <h1 className="font-serif text-5xl tracking-tight mt-2">{t("account.title")}</h1>

      <Tabs defaultValue="orders" className="mt-12">
        <TabsList className="bg-transparent border-b border-edge rounded-none h-auto p-0 w-full justify-start">
          <TabsTrigger value="orders" className="rounded-none border-b-2 border-transparent data-[state=active]:border-olive bg-transparent px-0 mr-8 pb-3 text-sm" data-testid="tab-orders">{t("account.tabOrders")} ({orders.length})</TabsTrigger>
          <TabsTrigger value="subs" className="rounded-none border-b-2 border-transparent data-[state=active]:border-olive bg-transparent px-0 mr-8 pb-3 text-sm" data-testid="tab-subs">{t("account.tabSubs")} ({subs.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="pt-10">
          {orders.length === 0 ? (
            <div className="bg-boneAlt rounded-sm p-16 text-center">
              <p className="font-serif text-2xl">{t("account.noOrders")}</p>
              <Link to="/shop" className="inline-block mt-6 bg-olive text-bone px-6 py-3 rounded-sm text-sm">{t("account.discover")}</Link>
            </div>
          ) : (
            <div className="space-y-6" data-testid="orders-list">
              {orders.map((o) => {
                const overall = o.fulfillment || "pending";
                return (
                  <Link
                    to={`/order/${o.order_id}`}
                    key={o.order_id}
                    className="block bg-white hairline rounded-sm p-6 hover:border-olive transition-colors"
                    data-testid={`order-${o.order_id}`}
                  >
                    <div className="flex justify-between items-center flex-wrap gap-2">
                      <div>
                        <p className="overline flex items-center gap-2">
                          {t("account.order")} {STATUS_ICON[overall]}<span data-testid={`order-fulfillment-${o.order_id}`}>{overall}</span>
                        </p>
                        <p className="font-mono text-sm mt-1">{o.order_id}</p>
                      </div>
                      <div className="text-right">
                        <p className="overline">{t("account.total")}</p>
                        <p className="font-medium">{formatPrice(o.amount, o.currency === "eur" ? "hr" : "en")}</p>
                      </div>
                    </div>
                    <div className="mt-6 space-y-2">
                      {o.items.map((it, i) => (
                        <div key={i} className="text-sm flex justify-between text-ink-soft">
                          <span>{it.quantity} × {it.title}</span>
                          <span>{formatPrice(it.price * it.quantity, o.currency === "eur" ? "hr" : "en")}</span>
                        </div>
                      ))}
                    </div>
                    {o.fulfillment_by_vendor && Object.values(o.fulfillment_by_vendor).some((v) => v.tracking_number) && (
                      <div className="mt-4 border-t border-edge pt-3 space-y-1" data-testid={`tracking-${o.order_id}`}>
                        {Object.entries(o.fulfillment_by_vendor).map(([vid, v]) => v.tracking_number && (
                          <p key={vid} className="text-xs text-olive">{v.carrier} · {v.tracking_number}</p>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-ink-muted mt-4">{formatDateTime(o.created_at, lang)} · {o.status}</p>
                  </Link>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="subs" className="pt-10">
          {subs.length === 0 ? (
            <div className="bg-boneAlt rounded-sm p-16 text-center">
              <p className="font-serif text-2xl">{t("account.noSubs")}</p>
              <Link to="/shop" className="inline-block mt-6 bg-olive text-bone px-6 py-3 rounded-sm text-sm">{t("account.browse")}</Link>
            </div>
          ) : (
            <div className="space-y-4" data-testid="subs-list">
              {subs.map((s) => (
                <div key={s.subscription_id} className="bg-white hairline rounded-sm p-6 flex justify-between items-center flex-wrap gap-4">
                  <div>
                    <p className="overline">{s.cadence} · {s.status}</p>
                    <h3 className="font-serif text-xl mt-1">{localized(s.product, "title", lang)}</h3>
                    <p className="text-sm text-ink-soft">{s.product?.vendor_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{formatPrice(s.unit_amount, s.currency === "eur" ? "hr" : "en")} <span className="text-xs text-ink-muted">{t("subscriptions.perDelivery")}</span></p>
                    <Link to="/subscriptions" className="text-xs text-olive underline mt-2 inline-block">{t("account.manage")}</Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
